import React, { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../utils/cn';
import { Home, List as ListIcon, Plus, Calendar, Users, ListChecks } from 'lucide-react';

interface MobileLayoutProps {
  children: ReactNode;
}

const tabs = [
  { name: 'Главная', href: '/', icon: Home },
  { name: 'Списки', href: '/lists', icon: ListIcon },
  { name: 'Создать', href: '/new-list', icon: Plus, primary: true },
  { name: 'Календарь', href: '/calendar', icon: Calendar },
  { name: 'Группы', href: '/groups', icon: Users },
];

const MobileLayout: React.FC<MobileLayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const initial = (user?.username || '?').charAt(0).toUpperCase();

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-surface-muted">
      <header className="app-header bg-surface/85 backdrop-blur border-b border-line">
        <div className="px-4">
          <div className="flex items-center h-14 gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-primary-600 text-white flex items-center justify-center">
                <ListChecks className="w-4.5 h-4.5" strokeWidth={2.25} />
              </div>
              <span className="text-base font-semibold tracking-tight text-ink">xLists</span>
            </Link>
            <div className="flex-1" />
            <button
              onClick={() => navigate('/profile')}
              className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center text-sm font-semibold"
              aria-label="Профиль"
            >
              {initial}
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 pb-20 app-main">{children}</main>

      <nav className="mobile-nav">
        <div className="grid grid-cols-5 max-w-[440px] mx-auto">
          {tabs.map(({ name, href, icon: Icon, primary }) => {
            const active = isActive(href);
            if (primary) {
              return (
                <Link
                  key={href}
                  to={href}
                  className="flex justify-center items-end pt-1 pb-1"
                  aria-label={name}
                >
                  <span className="inline-flex w-12 h-12 rounded-2xl bg-primary-600 text-white items-center justify-center shadow-lifted -mt-5 transition-transform active:scale-95">
                    <Icon className="w-5 h-5" strokeWidth={2.25} />
                  </span>
                </Link>
              );
            }
            return (
              <Link
                key={href}
                to={href}
                aria-label={name}
                className={cn(
                  'flex flex-col items-center justify-center py-2.5 gap-1 transition-colors',
                  active ? 'text-primary-600' : 'text-ink-subtle hover:text-ink'
                )}
              >
                <Icon className="w-5 h-5 shrink-0" strokeWidth={active ? 2.5 : 2} />
                <span
                  className={cn(
                    'h-1 w-1 rounded-full transition-colors',
                    active ? 'bg-primary-600' : 'bg-transparent'
                  )}
                />
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default MobileLayout;
